// Moves as data with frame data (decision 0051; suggestion "combat module: move tables with frame data as the single
// source of truth"). A move's timing is counted in fixed steps at the table's step rate: wind-up, active and recovery.
// The same numbers drive the hit volume (live only in the active steps), the lunge, the cancel windows, the
// telegraph's lead and the events the poses and trails read. The game writes the tables (JSON content); the package
// owns their shape (`MOVE_SCHEMA`, `WEAPON_SCHEMA`) and the arithmetic.

/** A vector as plain data: [x, y, z]. */
export type Vec3 = readonly [number, number, number];

/** A move's kind: a combo step, the finisher or a heavy blow, a skill, the charged big move. */
export type MoveKind = "light" | "heavy" | "skill" | "big";

/** What the input asks a fighter for (the combat port's `request`). */
export type IntentKind = "attack" | "skill" | "big" | "dodge" | "block" | "blockRelease";

/** A move's phase at a step. */
export type MovePhase = "windup" | "active" | "recovery" | "end";

/**
 * The hit volume: a ball of `radius` m at `height` m above the attacker's feet, swept over the active steps along an
 * arc about the attacker (angles in radians from its facing, positive the way a growing yaw turns, from +Z toward
 * +X; reach in metres from its centre). A slash is an arc at a fixed reach; a thrust or a bolt is a straight line out (arc 0, reach
 * growing); a slam is a big ball at reach 0.
 */
export interface HitVolume {
  radius: number;
  height: number;
  /** Reach at the start and the end of the active window (m). */
  reach: readonly [number, number];
  /** Angle at the start and the end of the active window (rad, from the facing). */
  arc: readonly [number, number];
  /** Where the volume is measured from: the attacker's feet as it moves ("self", the default), or the point its
   *  target stood on when the move started ("target": a curse or a strike on the ground, which a target escapes by
   *  moving off it; with no target, the attacker's feet). The telegraph is drawn there too. */
  anchor?: "self" | "target";
}

/** A telegraph's shape on the ground (the feedback stream draws it). */
export type TelegraphShape = { kind: "circle"; radius: number } | { kind: "cone"; radius: number; angle: number } | { kind: "line"; length: number; width: number };

/** A move's telegraph: shown from the wind-up's start until it lands. `style` names the game's telegraph style (its
 *  colour, fill pattern and edge), so an unblockable blow differs in colour, shape and pattern (contradiction 2). */
export interface TelegraphDef {
  shape: TelegraphShape;
  style: string;
  /** Metres ahead of the attacker's feet for the shape's origin. Default 0. */
  offset?: number;
}

/** A window in a move (steps from its start) in which some intents may cancel it. */
export interface CancelWindow {
  from: number;
  /** To this step (exclusive); default the move's end. */
  to?: number;
  into: readonly IntentKind[];
  /** Only once the move has landed a hit (a hit-confirm cancel). */
  onHit?: boolean;
}

/** One move, its timing in steps at the table's step rate. */
export interface MoveDef {
  id: string;
  kind: MoveKind;
  windup: number;
  active: number;
  recovery: number;
  /** A combo's next move, and the step (from this move's start) from which an attack press chains into it. */
  next?: string;
  chainFrom?: number;
  cancels?: readonly CancelWindow[];
  /** The ability multiplier on the attacker's attack. */
  power: number;
  /** Poise the target loses on a hit. */
  poise: number;
  /** For feedback, 0..1. */
  severity: number;
  /** Hit-stop on contact (real seconds; the clock combines overlaps by max). */
  hitStop: number;
  hit: HitVolume;
  /** The move drives its fighter forward at `speed` m/s from step `from` to `to`, stopping `stopAt` m from its target. */
  lunge?: { speed: number; from: number; to: number; stopAt: number };
  /** Steps (from the start) in which poise damage can't stagger the attacker (hyper armour). */
  armour?: readonly [number, number];
  /** Can't be blocked or parried, only dodged. */
  unblockable?: boolean;
  telegraph?: TelegraphDef;
  /** Big-move charge gained per landed hit (the `energy` attribute). */
  charge?: number;
  /** Damage-type tags for resistances ("damage.fire"). */
  damage?: readonly string[];
  /** Effects a landed hit (not one dodged, blocked or parried) applies to its target, each with a chance (default 1,
   *  rolled on the game's stream only when below 1): a burn, a slow, a stun (decision 0050). */
  applies?: readonly { effect: string; chance?: number }[];
}

/** A weapon's move set: a combo of light moves (the last may be heavy), a skill on a cooldown, a charged big move. */
export interface WeaponDef {
  id: string;
  /** The combo's moves in order (each one's `next` should name the following one). */
  combo: readonly string[];
  skill?: string;
  /** The skill's cooldown, seconds. */
  cooldown?: number;
  big?: string;
  /** The big move's charge cost (of `energy`). */
  bigCost?: number;
}

/** A move's total length in steps. */
export const moveSteps = (m: MoveDef): number => m.windup + m.active + m.recovery;

/** The phase at `step` steps from the move's start. */
export function phaseAt(m: MoveDef, step: number): MovePhase {
  if (step < m.windup) return "windup";
  if (step < m.windup + m.active) return "active";
  if (step < moveSteps(m)) return "recovery";
  return "end";
}

/** A move's timing retimed from the table's step rate to the sim's (each count rounded, never below 1 active step). */
export function retime(m: MoveDef, fromHz: number, toHz: number): MoveDef {
  if (fromHz === toHz) return m;
  const k = toHz / fromHz;
  const r = (n: number): number => Math.round(n * k);
  return {
    ...m,
    windup: r(m.windup),
    active: Math.max(1, r(m.active)),
    recovery: r(m.recovery),
    ...(m.chainFrom !== undefined ? { chainFrom: r(m.chainFrom) } : {}),
    ...(m.cancels ? { cancels: m.cancels.map((c) => ({ ...c, from: r(c.from), ...(c.to !== undefined ? { to: r(c.to) } : {}) })) } : {}),
    ...(m.lunge ? { lunge: { ...m.lunge, from: r(m.lunge.from), to: r(m.lunge.to) } } : {}),
    ...(m.armour ? { armour: [r(m.armour[0]), r(m.armour[1])] as const } : {}),
  };
}

/** The hit volume's centre at active step `i` (0 .. active - 1) for an attacker at `feet` facing `yaw` (0 faces +Z). */
export function volumeAt(m: MoveDef, i: number, feet: Vec3, yaw: number): Vec3 {
  const k = m.active <= 1 ? 1 : Math.min(1, Math.max(0, i / (m.active - 1)));
  const h = m.hit;
  const reach = h.reach[0] + (h.reach[1] - h.reach[0]) * k;
  const a = yaw + h.arc[0] + (h.arc[1] - h.arc[0]) * k;
  return [feet[0] + Math.sin(a) * reach, feet[1] + h.height, feet[2] + Math.cos(a) * reach];
}

/** Check a move table: every move's numbers in range, every combo, chain and weapon reference resolved. Issues as text. */
export function checkMoves(moves: readonly MoveDef[], weapons: readonly WeaponDef[] = [], effects?: readonly string[]): string[] {
  const out: string[] = [];
  const byId = new Map(moves.map((m) => [m.id, m]));
  const int = (n: number): boolean => Number.isInteger(n) && n >= 0;
  for (const m of moves) {
    if (!int(m.windup) || !int(m.recovery) || !int(m.active) || m.active < 1) out.push(`${m.id}: wind-up, active and recovery are whole steps, active ≥ 1`);
    if (m.next !== undefined && !byId.has(m.next)) out.push(`${m.id}: next "${m.next}" is not a move`);
    if (m.next !== undefined && (m.chainFrom === undefined || m.chainFrom < m.windup || m.chainFrom >= moveSteps(m))) out.push(`${m.id}: a combo step needs chainFrom inside its active or recovery steps`);
    if (m.lunge && (m.lunge.from < 0 || m.lunge.to > moveSteps(m) || m.lunge.from >= m.lunge.to)) out.push(`${m.id}: the lunge's steps fall outside the move`);
    if (m.hit.radius <= 0) out.push(`${m.id}: the hit volume needs a radius`);
    if (effects) for (const a of m.applies ?? []) if (!effects.includes(a.effect)) out.push(`${m.id}: applies unknown effect "${a.effect}"`);
  }
  for (const w of weapons) {
    if (!w.combo.length) out.push(`${w.id}: an empty combo`);
    for (const id of [...w.combo, ...(w.skill ? [w.skill] : []), ...(w.big ? [w.big] : [])]) if (!byId.has(id)) out.push(`${w.id}: "${id}" is not a move`);
    w.combo.forEach((id, i) => {
      const next = w.combo[i + 1];
      if (next !== undefined && byId.get(id)?.next !== next) out.push(`${w.id}: combo step "${id}" should chain into "${next}"`);
    });
  }
  return out;
}
